/**
 * Shared Design Tokens
 * Single source of truth for design system across web and mobile
 *
 * Web: Imported into Tailwind config
 * Mobile: Can be imported into React Native StyleSheet themes
 */

export const colors = {
  // Primary: Indigo scale
  primary: {
    DEFAULT: "#6366f1",
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1",
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
  },

  // Secondary: Teal/Emerald scale
  secondary: {
    DEFAULT: "#14b8a6",
    50: "#f0fdfa",
    100: "#ccfbf1",
    200: "#99f6e4",
    300: "#5eead4",
    400: "#2dd4bf",
    500: "#14b8a6",
    600: "#0d9488",
    700: "#0f766e",
    800: "#115e59",
    900: "#134e4a",
  },

  // Semantic colors
  accent: {
    DEFAULT: "#f59e0b",
    500: "#f59e0b",
  },

  success: {
    DEFAULT: "#22c55e",
    100: "#dcfce7",
    500: "#22c55e",
    700: "#15803d",
  },

  warning: {
    DEFAULT: "#eab308",
    100: "#fef9c3",
    500: "#eab308",
    700: "#a16207",
  },

  error: {
    DEFAULT: "#ef4444",
    100: "#fee2e2",
    500: "#ef4444",
    700: "#b91c1c",
  },

  // Grays
  gray: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827",
  },

  // Dark surfaces
  background: "#0b0f19", // Main app background
  surface: "#0f1424", // Cards, panels on dark bg

  // Common colors
  white: "#ffffff",
  black: "#000000",
  transparent: "transparent",
} as const;

export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
  28: 112,
  32: 128,
} as const;

export const typography = {
  fontSize: {
    xs: 12, // 0.75rem
    sm: 14, // 0.875rem
    base: 16, // 1rem
    lg: 18, // 1.125rem
    xl: 20, // 1.25rem
    "2xl": 36, // 2.25rem (updated for h2)
    "3xl": 48, // 3rem (updated for h1)
    "4xl": 24, // 1.5rem (updated for h4)
    "5xl": 30, // 1.875rem (updated for h3)
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  lineHeight: {
    none: 1,
    tight: 1.1,
    snug: 1.15,
    normal: 1.25,
    relaxed: 1.4,
    loose: 1.5,
    extraLoose: 1.6,
    superLoose: 1.75,
  },
} as const;

export const gradients = {
  heading: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  primary: "linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)",
  secondary: "linear-gradient(135deg, rgba(13, 148, 136, 0.9) 0%, rgba(20, 184, 166, 0.9) 100%)",
  ghost: "linear-gradient(90deg, #2dd4bf 0%, #14b8a6 100%)",
  danger: "linear-gradient(90deg, #f87171 0%, #b91c1c 100%)",
  default: "linear-gradient(90deg, #e5e7eb 0%, #d1d5db 100%)",
} as const;

export const borderRadius = {
  none: 0,
  sm: 2,
  base: 4,
  md: 12, // 0.75rem for buttons
  lg: 8,
  xl: 12,
  "2xl": 16,
  panel: 16, // 1rem for cards/panels
  full: 9999,
} as const;

export const shadows = {
  card: "0 6px 30px -8px rgba(0,0,0,0.45)",
} as const;

// Export types for TypeScript
export type Colors = typeof colors;
export type Spacing = typeof spacing;
export type Typography = typeof typography;
export type Gradients = typeof gradients;
export type BorderRadius = typeof borderRadius;
export type Shadows = typeof shadows;
